import { Component, DestroyRef, ElementRef, afterNextRender, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { gsap } from '../../../motion/gsap';
import { QUERIES } from '../../../motion/motion-env';
import { MotionService } from '../../../motion/motion.service';
import { translateLive } from '../../../services/i18n-live';
import { PackageCardVM } from '../../common/package-grid/package-grid.component';

/** Shape of `services.all` in assets/i18n/*.json. */
interface ServicePackage {
  type: string;
  goal?: string;
  info?: string[];
  price: string;
  img: string;
  bgColor: string;
  fontColor: string;
}

/** The part of the `home` translations the page header reads. */
interface HomeTexts {
  design?: { coursesIntro?: string };
  ourPackages?: string;
  'packages-intro'?: string;
}

@Component({
  selector: 'app-services',
  templateUrl: './services.component.html',
  styleUrl: './services.component.css',
})
export class Services {
  private readonly translate = inject(TranslateService);
  private readonly motion = inject(MotionService);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private mm?: ReturnType<typeof gsap.matchMedia>;
  texts = { title: '', intro: '' };
  cards: PackageCardVM[] = [];

  constructor() {
    inject(ActivatedRoute)
      .params.pipe(takeUntilDestroyed())
      .subscribe((params) => this.translate.use(params['lang'] || 'am'));

    // Copy and cards both come from translateLive: unlike the translate pipe, it cannot show a
    // stale language when two language loads overlap (e.g. a stored language and the URL's).
    translateLive<HomeTexts>(this.translate, 'home')
      .pipe(takeUntilDestroyed())
      // Same intro sentence as the home packages section.
      .subscribe(
        (home) => (this.texts = { title: home.ourPackages ?? '', intro: home.design?.coursesIntro ?? home['packages-intro'] ?? '' })
      );

    translateLive<ServicePackage[]>(this.translate, 'services.all')
      .pipe(takeUntilDestroyed())
      .subscribe((packages) => {
        this.cards = (Array.isArray(packages) ? packages : []).map((p) => ({
          type: p.type,
          goal: p.goal,
          lines: p.info ?? [],
          price: p.price,
          icon: p.img,
          bgColor: p.bgColor,
          fontColor: p.fontColor,
        }));
      });

    afterNextRender(() => this.motion.outside(() => this.setupRoad()));
    inject(DestroyRef).onDestroy(() => this.mm?.revert());
  }

  /**
   * The header road's lane dashes drive in once, then move only with the user's own scroll
   * (no autoplay loop). Both offsets feed one transform, so scrolling during the intro is smooth.
   */
  private setupRoad(): void {
    const hero = this.host.querySelector<HTMLElement>('.svc-hero');
    const dashes = hero?.querySelector<HTMLElement>('.road-dashes');
    if (!hero || !dashes) return;

    const mm = (this.mm = gsap.matchMedia(hero));
    mm.add({ motion: QUERIES.motion }, (ctx) => {
      if (!ctx.conditions!['motion']) return;

      const offset = { intro: -33.3, scroll: 0 };
      const setY = gsap.quickSetter(dashes, 'yPercent');
      const apply = () => setY(offset.intro + offset.scroll);
      apply();
      gsap.to(offset, { intro: 0, duration: 1.4, ease: 'expo.out', onUpdate: apply });
      gsap.to(offset, {
        scroll: 33.3,
        ease: 'none',
        onUpdate: apply,
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
      });
      return () => gsap.set(dashes, { clearProps: 'transform' });
    });
  }
}
